import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../../context/JourneyContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, ArrowRight, Clock, Users, Sparkles, Star, Loader2, Info } from 'lucide-react';
import axios from 'axios';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Options = () => {
  const navigate = useNavigate();
  const { journey, trackPageView, trackCtaClick } = useJourney();
  const [selectedOption, setSelectedOption] = useState(null);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageView('portal_options');
    fetchPrograms();
  }, [trackPageView, journey?.user_type]);

  const fetchPrograms = async () => {
    const userType = journey?.user_type || 'Primary or Intermediate School';
    try {
      const response = await axios.get(`${API}/programs/by-type`, {
        params: { user_type: userType }
      });
      setOptions(response.data.programs || []);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = (option) => {
    setSelectedOption(option.id);
    trackCtaClick(option.cta);
  };

  // Check if any option has a funding note (for schools)
  const hasFundingNote = options.some(opt => opt.funding_note);

  return (
    <div 
      data-testid="options-screen"
      className="min-h-screen relative overflow-hidden bg-[#1C1917]"
    >
      {/* Background - clean */}
      <div className="absolute top-40 right-0 w-80 h-80 bg-accent/5 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        {/* Navigation - always visible */}
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/portal/proof')}
          className="mb-8 text-stone-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          <div className="h-1 w-8 rounded-full bg-primary" />
          <div className="h-1 w-8 rounded-full bg-primary" />
          <div className="h-1 w-8 rounded-full bg-primary" />
          <div className="h-1 w-8 rounded-full bg-stone-700" />
          <span className="ml-4 text-sm text-stone-400">Step 3 of 4</span>
        </div>

        {/* Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h1 
            data-testid="options-title"
            className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4"
          >
            Your Options
          </h1>
          <p className="text-base sm:text-lg text-stone-400 max-w-xl mx-auto">
            Choose the program that fits your needs. Each option is designed to create meaningful impact.
          </p>
        </div>

        {/* Funding note for schools */}
        {hasFundingNote && (
          <div className="bg-accent/10 border border-accent/30 rounded-2xl p-4 mb-8 flex items-start gap-3 animate-fade-in-up">
            <Info className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
            <p className="text-sm text-stone-300">
              Some schools use external funding or sponsorship to support delivery.
            </p>
          </div>
        )}

        {/* Loading state */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Options cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {options.map((option, index) => (
                <Card
                  key={option.id}
                  data-testid={`option-card-${option.id}`}
                  onClick={() => handleSelectOption(option)}
                  className={cn(
                    "relative cursor-pointer p-6 rounded-3xl border-2 transition-all duration-300",
                    "hover:scale-[1.02] active:scale-[0.98]",
                    "animate-fade-in-up opacity-0",
                    selectedOption === option.id 
                      ? "border-primary bg-primary/10" 
                      : "border-stone-700 bg-stone-900/50 hover:border-primary/30"
                  )}
                  style={{ animationDelay: `${0.1 + index * 0.1}s`, animationFillMode: 'forwards' }}
                >
                  {/* Popular badge */}
                  {option.popular && (
                    <Badge className="absolute -top-3 left-6 bg-primary text-white">
                      <Star className="w-3 h-3 mr-1" />
                      Popular
                    </Badge>
                  )}

                  {/* Price with GST */}
                  <div className="mb-3">
                    <span className="text-lg text-primary font-bold">
                      {option.price}
                    </span>
                    {option.price_note && (
                      <span className="text-sm text-stone-400 ml-1">
                        {option.price_note}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-heading font-bold text-white mb-2">
                    {option.name}
                  </h3>

                  {/* Best for */}
                  <div className="flex items-center gap-2 text-stone-400 text-sm mb-4">
                    <Users className="w-4 h-4" />
                    <span>{option.best_for}</span>
                  </div>

                  {/* Duration */}
                  <div className="flex items-center gap-2 text-stone-400 text-sm mb-4">
                    <Clock className="w-4 h-4" />
                    <span>{option.duration}</span>
                  </div>

                  {/* Outcomes */}
                  <div className="space-y-2 mb-6">
                    {option.outcomes.map((outcome, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-stone-300">{outcome}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Button
                    className={cn(
                      "w-full rounded-full transition-all",
                      selectedOption === option.id
                        ? "bg-primary text-white"
                        : "bg-stone-800 text-stone-300 hover:bg-stone-700"
                    )}
                  >
                    {option.cta}
                  </Button>
                </Card>
              ))}
            </div>

            {/* No options message */}
            {options.length === 0 && (
              <div className="text-center py-12">
                <p className="text-stone-400">
                  Program options are being updated. Please contact us for current offerings.
                </p>
              </div>
            )}
          </>
        )}

        {/* Continue button */}
        <div className="text-center">
          <Button
            data-testid="continue-btn"
            onClick={() => navigate('/portal/next-step')}
            size="lg"
            className="rounded-full px-10 py-6 text-lg font-bold bg-primary hover:bg-primary/90 text-white transition-all duration-300 hover:scale-105"
          >
            Continue
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Options;
